import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Chip,
  IconButton,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, PersonOutline, Search, Phone, Upload, Download } from '@mui/icons-material';
import api from '../../utils/api';
import { Client, PropertyType, Role } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { getClientPermissions } from '../../utils/permissionUtils';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import AddClientDialog from '../../components/Clients/AddClientDialog';
import EditClientDialog from '../../components/Clients/EditClientDialog';
import ClientMatchingPropertiesDialog from '../../components/Clients/ClientMatchingPropertiesDialog';
import DataTableFilter from '../../components/Common/DataTableFilter';
import PageHeader from '../../components/Common/PageHeader';
import { useDataFilter } from '../../hooks/useDataFilter';
import ToastNotification from '../../utils/toastNotification';

const ClientsPage: React.FC = () => {
  const { user } = useAuth();

  // Derive permissions from current user
  const permissions = user?.permissions
    ? getClientPermissions(user.permissions)
    : { canCreate: false, canRead: false, canUpdate: false, canDelete: false };

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [matchingPropertiesDialogOpen, setMatchingPropertiesDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    isActive: '',
    minBudget: '',
    maxBudget: '',
    propertyTypes: [],
    governorate: '',
    district: '',
    town: '',
    paymentMethod: [],
    startDate: '',
    endDate: '',
  });
  // Only agents, admins and super-admins can toggle the called status
  const isAgent = !!user?.agentProfile;
  const isAdminOrAbove = user?.role === Role.SUPER_ADMIN || user?.role === Role.ADMIN;
  const canToggleCalled = isAgent || isAdminOrAbove;

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [importLoading, setImportLoading] = useState(false);
  const [importResultOpen, setImportResultOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ successes: number; errors: { row: number; message: string }[] } | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.id) return;

    try {
      await api.delete(`/clients/${deleteDialog.id}`);
      loadClients();
      setDeleteDialog({ open: false, id: null });
      ToastNotification.success('Client deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting client:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete client';
      ToastNotification.error(errorMessage);
    }
  };

  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setEditDialogOpen(true);
  };

  const handleToggleCalled = async (client: Client) => {
    try {
      await api.put(`/clients/${client.id}`, { isCalled: !client.isCalled });
      // Optimistically update local state so the UI reflects the change instantly
      setClients(prev =>
        prev.map(c => c.id === client.id ? { ...c, isCalled: !client.isCalled } : c)
      );
    } catch (error) {
      console.error('Error updating called status:', error);
    }
  };

  const handleViewMatchingProperties = (client: Client) => {
    setSelectedClient(client);
    setMatchingPropertiesDialogOpen(true);
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/import/clients', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = response.data;
      const successCount = Array.isArray(data.successes)
        ? data.successes.length
        : Array.isArray(data.created)
        ? data.created.length
        : typeof data.successes === 'number'
        ? data.successes
        : typeof data.created === 'number'
        ? data.created
        : 0;
      const errorList: { row: number; message: string }[] = Array.isArray(data.errors)
        ? data.errors.map((e: any) =>
            typeof e === 'string'
              ? { row: 0, message: e }
              : { row: e.row ?? 0, message: typeof e.message === 'string' ? e.message : JSON.stringify(e.message ?? e) }
          )
        : [];
      setImportResult({ successes: successCount, errors: errorList });
      setImportResultOpen(true);
      loadClients();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Import failed';
      setImportResult({ successes: 0, errors: [{ row: 0, message: msg }] });
      setImportResultOpen(true);
    } finally {
      setImportLoading(false);
    }
  };


  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const response = await api.get('/clients/export/csv', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting clients:', error);
      // You might want to show a toast notification here
    } finally {
      setExportLoading(false);
    }
  };
  const filterConfig = [
    {
      key: 'isActive',
      label: 'Status',
      type: 'select' as const,
      options: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
    },
    {
      key: 'minBudget',
      label: 'Min Budget',
      type: 'text' as const,
      placeholder: 'Minimum budget',
    },
    {
      key: 'maxBudget',
      label: 'Max Budget',
      type: 'text' as const,
      placeholder: 'Maximum budget',
    },
    {
      key: 'propertyTypes',
      label: 'Property Types',
      type: 'multiselect' as const,
      options: Object.values(PropertyType).map(type => ({
        label: type,
        value: type,
      })),
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      type: 'multiselect' as const,
      options: [
        { label: 'Cash', value: 'CASH' },
        { label: 'Government Housing', value: 'GOVERNMENT_HOUSING' },
        { label: 'Down Payment Facilities', value: 'DOWN_PAYMENT_FACILITIES' },
      ],
    },
    {
      key: 'startDate',
      label: 'Added From',
      type: 'date' as const,
    },
    {
      key: 'endDate',
      label: 'Added To',
      type: 'date' as const,
    },
  ];

  // Apply filters
  const filteredClients = useDataFilter(
    clients,
    searchValue,
    filterValues,
    {
      searchFields: ['firstName', 'lastName', 'email', 'phone', 'preferences', 'governorate', 'district', 'town'],
      filterFields: {
        isActive: (item, value) => !value || item.isActive === (value === 'true'),
        minBudget: (item, value) => !value || (item.minBudget && item.minBudget >= parseFloat(value)) || (item.budget && item.budget >= parseFloat(value)),
        maxBudget: (item, value) => !value || (item.maxBudget && item.maxBudget <= parseFloat(value)) || (item.budget && item.budget <= parseFloat(value)),
        propertyTypes: (item, value) => !value || (Array.isArray(value) && value.length === 0) || (Array.isArray(value) && item.propertyTypes && item.propertyTypes.some((type: string) => value.includes(type))),
        governorate: (item, value) => !value || (Array.isArray(item.governorate) ? item.governorate.some((g: string) => g.toLowerCase().includes(value.toLowerCase())) : !!(item.governorate && item.governorate.toLowerCase().includes(value.toLowerCase()))),
        district: (item, value) => !value || (Array.isArray(item.district) ? item.district.some((d: string) => d.toLowerCase().includes(value.toLowerCase())) : !!(item.district && item.district.toLowerCase().includes(value.toLowerCase()))),
        town: (item, value) => !value || !!(item.town && item.town.toLowerCase().includes(value.toLowerCase())),
        paymentMethod: (item, value) => !value || (Array.isArray(value) && value.length === 0) || (Array.isArray(value) && value.includes(item.paymentMethod)),
        startDate: (item, value) => !value || new Date(item.createdAt) >= new Date(value),
        endDate: (item, value) => !value || new Date(item.createdAt) <= new Date(value + 'T23:59:59'),
      },
    }
  );

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: 'Client',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => `${params.row.firstName} ${params.row.lastName}`,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.firstName} {params.row.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'budget',
      headerName: 'Budget',
      width: 180,
      renderCell: (params) => {
        if (params.row.minBudget && params.row.maxBudget) {
          return `$${params.row.minBudget.toLocaleString()} - $${params.row.maxBudget.toLocaleString()}`;
        } else if (params.row.minBudget) {
          return `From $${params.row.minBudget.toLocaleString()}`;
        } else if (params.row.maxBudget) {
          return `Up to $${params.row.maxBudget.toLocaleString()}`;
        } else if (params.row.budget) {
          return `$${params.row.budget.toLocaleString()}`;
        }
        return 'N/A';
      },
    },
    {
      field: 'propertyTypes',
      headerName: 'Property Types',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.row.propertyTypes && params.row.propertyTypes.length > 0 ? (
            params.row.propertyTypes.map((type: string) => (
              <Chip key={type} label={type} size="small" color="primary" variant="outlined" />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">None</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        const gov = Array.isArray(params.row.governorate) ? params.row.governorate.join(', ') : (params.row.governorate || '');
        const dist = Array.isArray(params.row.district) ? params.row.district.join(', ') : (params.row.district || '');
        const town = params.row.town || '';
        return [gov, dist, town].filter(Boolean).join(' / ') || 'N/A';
      },
      renderCell: (params) => {
        const gov = Array.isArray(params.row.governorate) ? params.row.governorate : (params.row.governorate ? [params.row.governorate] : []);
        const dist = Array.isArray(params.row.district) ? params.row.district : (params.row.district ? [params.row.district] : []);
        const town = params.row.town;
        const hasData = gov.length > 0 || dist.length > 0 || town;
        if (!hasData) return <Typography variant="caption" color="text.secondary">N/A</Typography>;
        return (
          <Box>
            {gov.length > 0 && (
              <Typography variant="caption" display="block" noWrap>{gov.join(', ')}</Typography>
            )}
            {dist.length > 0 && (
              <Typography variant="caption" display="block" noWrap color="text.secondary">{dist.join(', ')}</Typography>
            )}
            {town && (
              <Typography variant="caption" display="block" noWrap color="text.secondary">{town}</Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment Method',
      width: 160,
      renderCell: (params) => {
        const method = params.row.paymentMethod;
        if (!method) return <Typography variant="caption" color="text.secondary">N/A</Typography>;
        const labels: Record<string, string> = {
          CASH: 'Cash',
          GOVERNMENT_HOUSING: 'Gov. Housing',
          DOWN_PAYMENT_FACILITIES: 'Down Payment',
        };
        return <Chip label={labels[method] || method} size="small" variant="outlined" />;
      },
    },
    {
      field: 'transactionType',
      headerName: 'Transaction',
      width: 110,
      renderCell: (params) => {
        const t = params.row.transactionType;
        if (!t) return <Typography variant="caption" color="text.secondary">N/A</Typography>;
        return <Chip label={t} size="small" color={t === 'BUY' ? 'primary' : 'default'} variant="outlined" />;
      },
    },
    {
      field: 'agents',
      headerName: 'Agents',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const agentList = params.row.agents;
        if (!agentList || agentList.length === 0) {
          return <Typography variant="caption" color="text.secondary">Unassigned</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {agentList.map((ag: any) => (
              <Chip
                key={ag.id}
                label={ag.user ? `${ag.user.firstName} ${ag.user.lastName}` : 'Unknown'}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'preferences',
      headerName: 'Preferences',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="caption" noWrap>
          {params.row.preferences || 'None'}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.isActive ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'called',
      headerName: 'Called',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.isCalled ? 'Called' : 'Not Called'}
          size="small"
          color={params.row.isCalled ? 'success' : 'default'}
          icon={<Phone sx={{ fontSize: '14px !important' }} />}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canToggleCalled && (
            <Tooltip
              title={params.row.isCalled ? 'Mark as Not Called' : 'Mark as Called'}
              arrow
            >
              <IconButton
                size="small"
                color={params.row.isCalled ? 'success' : 'default'}
                onClick={() => handleToggleCalled(params.row)}
              >
                <Phone />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="View Matching Properties" arrow>
            <IconButton
              size="small"
              color="info"
              onClick={() => handleViewMatchingProperties(params.row)}
              sx={{
                '&:hover': {
                  bgcolor: 'info.lighter',
                },
              }}
            >
              <Search />
            </IconButton>
          </Tooltip>
          {permissions.canUpdate && (
            <Tooltip title="Edit Client" arrow>
              <IconButton size="small" color="primary" onClick={() => handleEdit(params.row)}>
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          {permissions.canDelete && (
            <Tooltip title="Delete Client" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteDialog({ open: true, id: params.row.id })}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Clients Management"
        subtitle="Manage client relationships, budgets, and property interests"
        icon={<PersonOutline sx={{ fontSize: 'inherit', color: 'primary.main' }} />}
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Clients' },
        ]}
        actions={
          permissions.canCreate ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  boxShadow: 2,
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                  '&:hover': { boxShadow: 4 },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Client</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Add</Box>
              </Button>
              <Button
                variant="outlined"
                startIcon={importLoading ? <CircularProgress size={16} /> : <Upload />}
                onClick={() => importFileInputRef.current?.click()}
                disabled={importLoading}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  borderColor: '#2e7d32',
                  color: '#2e7d32',
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                  '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Import from Excel</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Import</Box>
              </Button>
              <input
                ref={importFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleImportExcel}
              />
              <Button
                variant="outlined"
                startIcon={exportLoading ? <CircularProgress size={16} /> : <Download />}
                onClick={handleExportCSV}
                disabled={exportLoading}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  borderColor: '#ff6f00',
                  color: '#ff6f00',
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                  '&:hover': { backgroundColor: 'rgba(255, 111, 0, 0.08)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export CSV</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Export</Box>
              </Button>
            </Box>
          ) : undefined
        }
      />

      {/* Filter Bar */}
      <Box sx={{ mb: 3 }}>
        <DataTableFilter
          filters={filterConfig}
          onFiltersChange={setFilterValues}
          onSearchChange={setSearchValue}
          searchPlaceholder="Search by name, email, phone, preferences..."
          showSearchBar={true}
          showAdvancedFilters={true}
          initialFilterValues={{ isActive: '', minBudget: '', maxBudget: '', propertyTypes: [], startDate: '', endDate: '' }}
          onRefresh={() => {
            setSearchValue('');
            setFilterValues({ isActive: '', minBudget: '', maxBudget: '', propertyTypes: [], startDate: '', endDate: '' });
          }}
          disabled={loading}
        />
      </Box>

      <Paper
        sx={{
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <DataGrid
          rows={filteredClients}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            minHeight: 500,
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f8f9fa',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9f9f9' },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'auto',
            },
          }}
        />
      </Paper>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, id: null })}
        confirmText="Delete"
        confirmColor="error"
      />

      <AddClientDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={loadClients}
      />

      <EditClientDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedClient(null);
        }}
        onSuccess={loadClients}
        client={selectedClient}
      />

      <ClientMatchingPropertiesDialog
        open={matchingPropertiesDialogOpen}
        onClose={() => {
          setMatchingPropertiesDialogOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
      />

      {/* Import Result Dialog */}
      <Dialog open={importResultOpen} onClose={() => setImportResultOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Import Result</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert severity={importResult.errors.length === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {importResult.successes} client{importResult.successes === 1 ? '' : 's'} imported successfully.
                {importResult.errors.length > 0 && ` ${importResult.errors.length} row(s) had errors.`}
              </Alert>
              {importResult.errors.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Errors:</Typography>
                  <List dense sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {importResult.errors.map((err, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemText
                          primary={err.row ? `Row ${err.row}` : 'Error'}
                          secondary={err.message}
                          primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResultOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsPage;
